import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  ReactNode,
} from "react";
import { Professional } from "../models/Professional";
import { ProfessionalService } from "../services/ProfessionalService";
import { useAuth } from "./AuthContext";
import { useMatter } from "./MatterContext";
import { useSocketEventByIds } from "../hooks/useSocketEventByIds";

interface Errors {
  list?: Error | null;
  details?: Error | null;
}
interface Loadings {
  list?: boolean;
  details?: boolean;
}

interface ProfessionalContextProps {
  professionals: Professional[];
  selectedProfessional: Professional | null;
  setSelectedProfessional: (professionalId: string) => void;
  createProfessional: (
    file: globalThis.File | null,
    professionalData: Omit<
      Professional,
      "_id" | "createdAt" | "updatedAt" | "createdBy" | "updatedBy" | "name"
    >,
  ) => Promise<string>;
  updateProfessional: (
    professionalId: string,
    file: globalThis.File | null,
    updatedData: Partial<
      Omit<Professional, "_id" | "createdAt" | "updatedAt" | "updatedBy">
    >,
  ) => Promise<void>;
  deleteProfessionalById: (professionalId: string) => void;
  createInvitation: (
    professionalId: string,
    date: { message: string },
  ) => Promise<void>;
  loading: Loadings;
  error: Errors;
}

const ProfessionalContext = createContext<ProfessionalContextProps | undefined>(
  undefined,
);

export const useProfessional = (): ProfessionalContextProps => {
  const context = useContext(ProfessionalContext);
  if (!context) {
    throw new Error(
      "useProfessional must be used within a ProfessionalProvider",
    );
  }
  return context;
};

export const ProfessionalProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const { currentMatterId } = useMatter();
  const { user } = useAuth();

  const [professionals, setProfessionals] = useState<Professional[]>([]);
  const [selectedProfessional, setSelectedProfessional] =
    useState<Professional | null>(null);
  const [errors, setErrors] = useState<Errors>({});
  const [loadings, setLoadings] = useState<Loadings>({
    list: false,
    details: false,
  });

  useSocketEventByIds(
    "professionals:changed",
    ["*"],
    (updatedDoc: Professional) => {
      setProfessionals((prev) =>
        prev.map((p) => (p._id === updatedDoc._id ? updatedDoc : p)),
      );
      if (selectedProfessional?._id === updatedDoc._id) {
        setSelectedProfessional(updatedDoc);
      } else {
        fetchProfessionals();
      }
    },
  );

  const fetchProfessionals = async () => {
    if (!user) return;
    try {
      const list = await ProfessionalService.getAll();
      setProfessionals(list);
      setErrors((prev) => ({ ...prev, list: null }));
    } catch (err: any) {
      setErrors((prev) => ({ ...prev, list: err }));
    } finally {
      setLoadings((prev) => ({ ...prev, list: false }));
    }
  };

  useEffect(() => {
    if (!user) return;
    setLoadings((prev) => ({ ...prev, list: true }));
    fetchProfessionals();
  }, [user]);

  const setSelectedProfessionalById = async (professionalId: string) => {
    if (!currentMatterId) return;
    try {
      setLoadings((prev) => ({ ...prev, details: true }));
      const professionalData =
        await ProfessionalService.getProfessional(professionalId);
      setSelectedProfessional(professionalData);
      setErrors((prev) => ({ ...prev, details: null }));
    } catch (err: any) {
      setSelectedProfessional(null);
      setErrors((prev) => ({ ...prev, details: err }));
    } finally {
      setLoadings((prev) => ({ ...prev, details: false }));
    }
  };

  const createProfessional = async (
    file: globalThis.File | null,
    professionalData: Omit<
      Professional,
      "_id" | "createdAt" | "updatedAt" | "createdBy" | "updatedBy" | "name"
    >,
  ): Promise<string> => {
    const newId = await ProfessionalService.createProfessional(
      file,
      professionalData,
    );
    return newId;
  };

  const updateProfessional = async (
    professionalId: string,
    file: globalThis.File | null,
    updatedData: Partial<
      Omit<Professional, "_id" | "createdAt" | "updatedAt" | "createdBy">
    >,
  ) => {
    if (!currentMatterId) return;
    await ProfessionalService.updateProfessional(
      professionalId,
      file,
      updatedData,
    );
    setSelectedProfessionalById(professionalId);
  };

  const deleteProfessionalById = async (professionalId: string) => {
    await ProfessionalService.deleteProfessional(professionalId);
    setProfessionals((prev) => prev.filter((p) => p._id !== professionalId));
    setSelectedProfessional(null);
  };

  const createInvitation = async (
    professionalId: string,
    date: { message: string },
  ) => {
    if (!currentMatterId) return;
    await ProfessionalService.createInvitation(professionalId, {
      ...date,
      matter: currentMatterId!,
    });
  };

  return (
    <ProfessionalContext.Provider
      value={{
        professionals,
        selectedProfessional,
        setSelectedProfessional: setSelectedProfessionalById,
        createProfessional,
        updateProfessional,
        deleteProfessionalById,
        createInvitation,
        loading: loadings,
        error: errors,
      }}
    >
      {children}
    </ProfessionalContext.Provider>
  );
};
